import { FeedbackOptionResource, Id, ImageResource, LanguageResource, TranslationEntry } from "@dgs/core";
import { CostCategoryOptionResource } from "../../../../shared/types/costCategory";
import { EventVisibility } from "../../../../shared/types/event";
import {
	CostCategoryRequirement,
	EventTypeRequirement,
	UserResponsibilityRequirement,
} from "../../../../shared/types/eventType";
import { UserResponsibilityResource } from "../../../../shared/types/userResponsibility";

export enum EventRole {
	ATTENDEE = "attendee",
	EXHIBITOR = "exhibitor",
	SPONSOR = "sponsor",
	ORGANIZER = "organizer",
}

export interface EventTypeIndexResource {
	id: Id;
	name: string;
	isDefault: boolean;
	eventRole: EventRole;
	color: string | null;
	isEventEditableByAllUsers: boolean;
}

export interface EventTypeUpdateResource {
	id: Id;
	eventRole: EventRole;
	fallbackLanguage: LanguageResource;
	isDefault: boolean;
	isEventEditableByAllUsers: boolean;
	requiresParticipationApproval: boolean;
	color: string | null;
	image: ImageResource;
	translations: TranslationEntry<EventTypeTranslationEntry>[];
	eventLanguageRequirement: EventTypeRequirement;
	locationRequirement: EventTypeRequirement;
	addressRequirement: EventTypeRequirement;
	organizerRequirement: EventTypeRequirement;
	plannedPersonCountRequirement: EventTypeRequirement;
	formatRequirement: EventTypeRequirement;
	eventCategoryRequirement: EventTypeRequirement;
	industryRequirement: EventTypeRequirement;
	targetGroupRequirement: EventTypeRequirement;
	regionRequirement: EventTypeRequirement;
	primaryOrganizationalUnitRequirement: EventTypeRequirement;
	organizationalUnitRequirement: EventTypeRequirement;
	additionalInfoRequirement: EventTypeRequirement;
	isHighlightRequirement: EventTypeRequirement;
	participationPermissionRequirement: EventTypeRequirement;
	linkToEventRequirement: EventTypeRequirement;
	tagRequirement: EventTypeRequirement;
	preferenceRequirement: EventTypeRequirement;
	eventGoalRequirement: EventTypeRequirement;

	visitorsMetricRequirement: EventTypeRequirement;
	invitedPersonsMetricRequirement: EventTypeRequirement;
	registrationsMetricRequirement: EventTypeRequirement;
	customersMetricRequirement: EventTypeRequirement;
	employeesMetricRequirement: EventTypeRequirement;
	eventReachMetricRequirement: EventTypeRequirement;
	eventGoalRatingMetricRequirement: EventTypeRequirement;

	costCategoryRequirements: CostCategoryRequirement[];
	costCategories: CostCategoryOptionResource[];
	userResponsibilityRequirements: UserResponsibilityRequirement[];
	userResponsibilities: UserResponsibilityResource[];
	feedback: FeedbackOptionResource[];
	eventGoalRatingMetricWeight: number;
	showFeedbackEvaluation: boolean;
	allowedVisibilities: EventVisibility[];
}

export interface EventTypeBaseFormValues {
	eventRole: EventRole | null;
	fallbackLanguage: LanguageResource | null;
	isDefault: boolean;
	color: string | null;
	translations: TranslationEntry<EventTypeTranslationEntry>[];
	image: ImageResource | null;
	isEventEditableByAllUsers: boolean;
	requiresParticipationApproval: boolean;
	showFeedbackEvaluation: boolean;
	feedback: FeedbackOptionResource[];
	allowedVisibilities: EventVisibility[];
}

export const eventTypeStaticRequirements = [
	"eventLanguageRequirement",
	"locationRequirement",
	"addressRequirement",
	"organizerRequirement",
	"plannedPersonCountRequirement",
	"formatRequirement",
	"eventCategoryRequirement",
	"industryRequirement",
	"targetGroupRequirement",
	"regionRequirement",
	"primaryOrganizationalUnitRequirement",
	"organizationalUnitRequirement",
	"additionalInfoRequirement",
	"isHighlightRequirement",
	"participationPermissionRequirement",
	"linkToEventRequirement",
	"tagRequirement",
	"preferenceRequirement",
	"eventGoalRequirement",
] as const;

export const eventTypeMetricRequirements = [
	"visitorsMetricRequirement",
	"invitedPersonsMetricRequirement",
	"registrationsMetricRequirement",
	"customersMetricRequirement",
	"employeesMetricRequirement",
	"eventReachMetricRequirement",
	"eventGoalRatingMetricRequirement",
] as const;

type EventTypeSettingsMetricRequirementFields = {
	[K in (typeof eventTypeMetricRequirements)[number]]: EventTypeRequirement;
};

type EventTypeSettingsStaticRequirementFields = {
	[K in (typeof eventTypeStaticRequirements)[number]]: EventTypeRequirement;
};
export interface EventTypeSettingsFormValues
	extends EventTypeSettingsStaticRequirementFields, EventTypeSettingsMetricRequirementFields {
	costCategoryRequirements: CostCategoryRequirement[];
	costCategories: CostCategoryOptionResource[];
	userResponsibilityRequirements: UserResponsibilityRequirement[];
	userResponsibilities: UserResponsibilityResource[];
	eventGoalRatingMetricWeight: number;
}

export type EventTypeFormValues = EventTypeBaseFormValues & EventTypeSettingsFormValues;

export interface CostCategoryRequirementRequest {
	costCategoryId: Id;
	requirement: EventTypeRequirement;
}

export interface UserResponsibilityRequirementRequest {
	userResponsibilityId: Id;
	requirement: EventTypeRequirement;
}

export interface EventTypeSaveRequest
	extends EventTypeSettingsStaticRequirementFields, EventTypeSettingsMetricRequirementFields {
	imageId: Id | null;
	eventRole: EventRole;
	fallbackLanguageIsoCode: string;
	isDefault: boolean;
	requiresParticipationApproval: boolean;
	color: string | null;
	translations: TranslationEntry<EventTypeTranslationEntry>[];
	costCategoryRequirements: CostCategoryRequirementRequest[];
	userResponsibilityRequirements: UserResponsibilityRequirementRequest[];
	feedbackIds: Id[];
	eventGoalRatingMetricWeight: number;
	showFeedbackEvaluation: boolean;
	allowedVisibilities: EventVisibility[];
}

export interface EventTypeTranslationEntry {
	name: string;
}
